import * as R from 'remeda';
import { Queue } from '@rossum/api-client/queues';
import { AnnotationStatus, MetaField } from '@rossum/api-client/shared';
import { GridFilterItem } from '@rossum/ui/x-data-grid-pro';

export type StatusTabKeys =
  | Extract<
      AnnotationStatus,
      'confirmed' | 'postponed' | 'rejected' | 'deleted' | 'in_workflow'
    >
  | 'reviews'
  | 'exports';

type GridFilterTab = Omit<GridFilterItem, 'value'> & {
  value: AnnotationStatus[];
};
export type StatusTab = {
  key: StatusTabKeys;
  filter: GridFilterTab;
  getValidCount: (context: Queue['counts']) => number;
  getInvalidCount: (context: Queue['counts']) => number;
};
export type StatusTabs = Partial<Record<StatusTabKeys, StatusTab>>;
export type StatusTabArray = StatusTab[];

export const STATUS_TABS_HEIGHT = 45;

export const statusTabSortOrder: StatusTabKeys[] = [
  'reviews',
  'postponed',
  'in_workflow',
  'rejected',
  'confirmed',
  'exports',
  'deleted',
];

export const createStatusFilter = (values: AnnotationStatus[]) => ({
  field: 'status',
  value: values,
  operator: 'isAnyOf',
});

export const defaultStatusTabs = {
  reviews: {
    key: 'reviews',
    filter: createStatusFilter([
      'to_review',
      'reviewing',
      'importing',
      'failed_import',
    ]),
    getValidCount: ({ to_review, reviewing, importing }) =>
      R.sum([to_review ?? 0, reviewing ?? 0, importing ?? 0]),
    getInvalidCount: ({ failed_import }) => failed_import ?? 0,
  },
  exports: {
    key: 'exports',
    filter: createStatusFilter(['exported', 'exporting', 'failed_export']),
    getValidCount: () => 0,
    getInvalidCount: ({ failed_export }) => failed_export ?? 0,
  },
  postponed: {
    key: 'postponed',
    filter: createStatusFilter(['postponed']),
    getValidCount: ({ postponed }) => postponed ?? 0,
    getInvalidCount: () => 0,
  },
  deleted: {
    key: 'deleted',
    filter: createStatusFilter(['deleted']),
    getValidCount: () => 0,
    getInvalidCount: () => 0,
  },
} as const satisfies StatusTabs;

export const workflowsTab: StatusTab = {
  key: 'in_workflow',
  filter: createStatusFilter(['in_workflow']),
  getValidCount: ({ in_workflow }) => in_workflow ?? 0,
  getInvalidCount: () => 0,
};

export const confirmedTab: StatusTab = {
  key: 'confirmed',
  filter: createStatusFilter(['confirmed']),
  getValidCount: ({ confirmed }) => confirmed ?? 0,
  getInvalidCount: () => 0,
};

export const rejectedTab: StatusTab = {
  key: 'rejected',
  filter: createStatusFilter(['rejected']),
  getValidCount: ({ rejected }) => rejected ?? 0,
  getInvalidCount: () => 0,
};

export const moveToReviewTabs: ReadonlySet<StatusTabKeys> = new Set([
  'confirmed',
  'rejected',
  'exports',
  'deleted',
  'in_workflow',
]);

export const statusTabsColumnMap = {
  modified_at: ['reviews', 'postponed'],
  modifier: ['reviews', 'postponed'],
  rejected_at: ['rejected'],
  rejected_by: ['rejected'],
  exported_at: ['exports'],
  exported_by: ['exports'],
  confirmed_at: ['confirmed'],
  confirmed_by: ['confirmed'],
  deleted_at: ['deleted'],
  deleted_by: ['deleted'],
  assigned_at: ['in_workflow'],
  assignees: ['in_workflow'],
} as const satisfies Partial<Record<MetaField, ReadonlyArray<StatusTabKeys>>>;
